import React, { StrictMode, Suspense } from 'react';

import { createBrowserHistory } from 'history';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { createBrowserRouter, createRoutesFromElements, redirect, Route, RouterProvider } from 'react-router-dom';

import LoadingDisplay from '@nhl/nhl-react-ui/lib/LoadingDisplay';

import configureStore from './redux/store';
import updateMobileParams from './utility/updateMobileParams';
import App from './views/layouts/app/App';

import GlobalStyle from './styles';

// Tell webpack where the public path is
// eslint-disable-next-line no-undef,camelcase
__webpack_public_path__ = window.__BASE_ASSETS_URL__;

// Get nonce from the root javascript bundle that is generated when we build our client code
const cspNonce = document.querySelector('#root-bundle').nonce;

// NB: Do not alter this line! I know your IDE is complaining. This will be used by styled-components to set a nonce
// attribute on injected CSS. It has to be set 'globally' but doing window.__webpack_nonce__ doesn't work for some reason.
__webpack_nonce__ = cspNonce;

// Highlight the stats nav item
const matchingNavItems = document.querySelectorAll('.nhl-o-menu__link[href$="stats"]');
matchingNavItems.forEach((navItem) => {
    // eslint-disable-next-line no-param-reassign
    navItem.className = `${navItem.className} -current`;
});

const rootElement = document.getElementById('root');

const basename = `/stats${window.__SITE_LANGUAGE_CODE__ === 'en' ? '' : `/${window.__SITE_LANGUAGE_CODE__}`}`;

const history = createBrowserHistory({ basename });

// TODO: Tell DSS to use the correct params, and urls
// The mobile app calls /stats/mobile and provides params we are no longer using so we are remapping them using this
// func
history.location = updateMobileParams(history);
if (history.location.mobilePath) {
    history.replace(history.location.mobilePath, {});
}

const initializeApp = () => {
    if (rootElement != null) {
        const store = configureStore();

        const root = createRoot(rootElement);

        const routes = [
            <Route
                key="skaters"
                path="/skaters"
                lazy={() => import(/* webpackChunkName: "Reports" */ '~/views/pages/reports/Reports')}
            />,
            <Route
                key="skater"
                path="/skater"
                loader={() => {
                    return redirect('/skaters');
                }}
            />,
            <Route
                key="goalies"
                path="/goalies"
                lazy={() => import(/* webpackChunkName: "Reports" */ '~/views/pages/reports/Reports')}
            />,
            <Route
                key="goalie"
                path="/goalie"
                loader={() => {
                    return redirect('/goalies');
                }}
            />,
            <Route
                key="teams"
                path="/teams"
                lazy={() => import(/* webpackChunkName: "Reports" */ '~/views/pages/reports/Reports')}
            />,
            <Route
                key="team"
                path="/team"
                loader={() => {
                    return redirect('/teams');
                }}
            />,
            <Route
                key="glossary"
                path="/glossary"
                lazy={() => import(/* webpackChunkName: "Glossary" */ '~/views/pages/glossary/Glossary')}
            />,
            <Route
                key="milestone"
                path="/milestone"
                lazy={() => import(/* webpackChunkName: "Milestone" */ '~/views/pages/milestones/Milestone')}
            />,
            <Route
                key="shiftcharts"
                path="/shiftcharts"
                lazy={() => import(/* webpackChunkName: "ShiftCharts" */ '~/views/pages/shiftCharts/ShiftCharts')}
            />,
            <Route
                key="performance-stats"
                path="/performance-stats"
                lazy={() =>
                    import(
                        /* webpackChunkName: "PuckAndPlayerTracking" */ '~/views/pages/puckAndPlayerTracking/PuckAndPlayerTracking'
                    )
                }
            />,
            <Route
                key="english"
                path="/en/:pathname?"
                loader={(props) => {
                    return redirect(`/${props.params.pathname || ''}`);
                }}
            />,
            <Route key="home" path="/" lazy={() => import(/* webpackChunkName: "Home" */ '~/views/pages/home/Home')} />,
        ];

        const router = createBrowserRouter(
            createRoutesFromElements(
                <Route path="/" element={<App />}>
                    {routes}
                </Route>,
            ),
            {
                basename,
            },
        );

        root.render(
            <StrictMode>
                <Suspense fallback={<LoadingDisplay isLoading />}>
                    <Provider store={store}>
                        <GlobalStyle />
                        <RouterProvider router={router} fallbackElement={<LoadingDisplay isLoading />} />
                    </Provider>
                </Suspense>
            </StrictMode>,
        );
    }
};

// Initializing rollbar
if (process.env.NODE_ENV === 'production') {
    import(/* webpackChunkName: "rollbar" */ `rollbar`).then(({ default: rollbar }) => {
        window.Rollbar = rollbar.init(window.rollbarConfig);
        initializeApp();
    });
} else {
    initializeApp();
}
